import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Backdrop,
  Avatar
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import configServ from '../../services/config';

// Date Formatter Helper: Displays DD/MM/YYYY while keeping YYYY-MM-DD behind the scenes
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`; // DD/MM/YYYY
  }
  return dateStr;
};

const BulkCaseHearingUpdatePage = () => {
  // 1. Header & Page Control State
  const [selectedDate, setSelectedDate] = useState('2026-08-01');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // 2. Data & Form State
  const [hearingProceedings, setHearingProceedings] = useState([]);
  const [formData, setFormData] = useState([]);

  // FETCH PROCEEDINGS
  const fetchHearingProceedings = async (date) => {
    setLoading(true);
    setSuccessMsg('');
    setErrorMsg('');

    const payload = {
      company_id: 1,
      hearing_date: date || selectedDate,
    };

    try {
      const response = await configServ.getHearingProceedings(payload);
      const dataList = response?.data?.data || response?.data || [];

      setHearingProceedings(dataList);

      // Pre-fill form entries
      const initialForm = dataList.map((item) => ({
        hearing_id: item.hearing_id,
        case_id: item.case_id,
        court_room: item.court_room || '',
        judge_name: item.judge_name || '',
        hearing_status: item.hearing_status || 'Pending',
        summary: item.summary || '',
        order_details: item.order_details || '',
        next_hearing_date: item.next_hearing_date || '',
      }));

      setFormData(initialForm);
    } catch (error) {
      console.error('Error fetching proceedings:', error);
      setErrorMsg('Hearing proceedings fetch karne me problem aayi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHearingProceedings(selectedDate);
  }, []);

  // INPUT HANDLER
  const handleInputChange = (index, field, value) => {
    const updatedForm = [...formData];
    updatedForm[index][field] = value;
    setFormData(updatedForm);
  };

  // SAVE & UPDATE HANDLER
  const handleSubmit = async () => {
    setSubmitting(true);
    setSuccessMsg('');
    setErrorMsg('');

    const finalPayload = {
      company_id: 1,
      created_by: 1,
      hearings: formData,
    };

    console.log('--- BULK UPDATE PAYLOAD ---');
    console.log(JSON.stringify(finalPayload, null, 2));

    try {
      const response = await configServ.bulkUpdateHearings(finalPayload);
      const apiResponse = response?.data || response;

      if (apiResponse?.status) {
        setSuccessMsg(apiResponse.message || 'Hearings updated successfully.');
        fetchHearingProceedings(selectedDate);
      } else {
        setErrorMsg(apiResponse?.message || 'Bulk update failed.');
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
      setErrorMsg('Proceedings update karte waqt error aayi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* FULL-PAGE BACKDROP LOADER FOR SUBMIT */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}
        open={submitting}
      >
        <CircularProgress color="inherit" size={50} />
        <Typography variant="h6">Bulk Hearing Updates Record Honge...</Typography>
      </Backdrop>

      {/* FILTER & HEADER PANEL */}
      <Paper elevation={4} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2, bgcolor: '#ffffff' }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <GavelIcon color="primary" sx={{ fontSize: 30 }} />
          <Typography variant="h5" fontWeight="700" color="#1a365d">
            Bulk Case Hearing Proceedings
          </Typography>
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Select Prev. Hearing Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              onClick={() => fetchHearingProceedings(selectedDate)}
              disabled={loading}
              sx={{
                bgcolor: '#1a365d',
                '&:hover': { bgcolor: '#0f2444' },
                height: '40px',
                fontWeight: 'bold',
              }}
            >
              {loading ? 'Fetching...' : 'Fetch Cause List'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ALERTS */}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}

      {/* LISTINGS / PROCEEDINGS */}
      {loading ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress color="primary" size={40} />
          <Typography color="textSecondary" sx={{ mt: 2 }}>
            Proceedings Fetch Ho Rahi Hain...
          </Typography>
        </Paper>
      ) : formData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">Selected date ke liye koi Cause List nahi mili.</Typography>
        </Paper>
      ) : (
        formData.map((item, index) => {
          const rawItem = hearingProceedings[index] || {};
          const displayPrevDate = formatDisplayDate(rawItem.hearing_date || selectedDate);
          const clientsList =
            rawItem.clients?.map((c) => c.client_name?.trim()).filter(Boolean).join(', ') || 'N/A';

          return (
            <Card
              key={item.hearing_id || index}
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: 3,
                border: '1px solid #dcdcdc',
                overflow: 'hidden',
              }}
            >
              {/* NAVY BLUE PROFESSIONAL HEADER BAR */}
              <Box
                sx={{
                  bgcolor: '#1a365d', // Deep Legal Blue Header
                  color: '#ffffff', // Crisp White Text
                  p: { xs: 1.5, sm: 2 },
                }}
              >
                <Grid container spacing={1.5} alignItems="center">
                  {/* S.NO BADGE */}
                  <Grid item xs={2} sm={1} md={0.8}>
                    <Avatar
                      sx={{
                        bgcolor: '#ffffff',
                        color: '#1a365d',
                        fontWeight: 'bold',
                        width: 34,
                        height: 34,
                        fontSize: '0.9rem',
                      }}
                    >
                      {index + 1}
                    </Avatar>
                  </Grid>

                  {/* CASE NO & TITLE */}
                  <Grid item xs={10} sm={5} md={3.2}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Case Reference
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" noWrap>
                      {rawItem.case_no} | {rawItem.case_title}
                    </Typography>
                  </Grid>

                  {/* FORMER / PREV HEARING DATE IN DD/MM/YYYY */}
                  <Grid item xs={6} sm={3} md={2}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarMonthIcon sx={{ fontSize: 16, opacity: 0.9 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Prev. Date
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {displayPrevDate}
                    </Typography>
                  </Grid>

                  {/* COURT NAME */}
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Forum / Court
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {rawItem.court_name || 'N/A'}
                    </Typography>
                  </Grid>

                  {/* OPPOSITE PARTY */}
                  <Grid item xs={6} sm={6} md={2}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Opp. Party
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {rawItem.opposite_party || 'N/A'}
                    </Typography>
                  </Grid>

                  {/* CLIENT LIST */}
                  <Grid item xs={6} sm={6} md={2}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Client(s)
                    </Typography>
                    <Chip
                      label={clientsList}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: '#ffffff',
                        fontWeight: 500,
                        maxWidth: '100%',
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* EDITABLE FORM SECTION */}
              <CardContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fafafa' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Court Room / Bench"
                      value={item.court_room}
                      onChange={(e) => handleInputChange(index, 'court_room', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Judge / Presiding Officer"
                      value={item.judge_name}
                      onChange={(e) => handleInputChange(index, 'judge_name', e.target.value)}
                    />
                  </Grid>

                  {/* <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Hearing Status"
                      value={item.hearing_status}
                      onChange={(e) => handleInputChange(index, 'hearing_status', e.target.value)}
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Adjourned">Adjourned</MenuItem>
                      <MenuItem value="Disposed">Disposed</MenuItem>
                    </TextField>
                  </Grid> */}

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="date"
                      size="small"
                      label="Next Listing Date"
                      value={item.next_hearing_date || ''}
                      onChange={(e) => handleInputChange(index, 'next_hearing_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText={
                        item.next_hearing_date
                          ? `Format: ${formatDisplayDate(item.next_hearing_date)}`
                          : 'Select next date'
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      label="Hearing Summary / Minutes"
                      value={item.summary}
                      onChange={(e) => handleInputChange(index, 'summary', e.target.value)}
                      placeholder="Brief argument notes..."
                    />
                  </Grid>

                  {/* <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      label="Order / Direction Details"
                      value={item.order_details}
                      onChange={(e) => handleInputChange(index, 'order_details', e.target.value)}
                      placeholder="Court directions or interim orders..."
                    />
                  </Grid> */}
                </Grid>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* FLOATING / BOTTOM ACTION BAR */}
      {formData.length > 0 && (
        <Box display="flex" justifyContent="flex-end" sx={{ mt: 3, mb: 4 }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ px: 5, py: 1.3, fontWeight: 'bold', borderRadius: 2 }}
          >
            Save & Bulk Update Proceedings
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default BulkCaseHearingUpdatePage;